// פריסות ושאילתת פריסה (06 §1–2).
import { z } from 'zod';
import { Id, Key, Row, Ts, rowBase } from './common.ts';

export const SpreadStatus = z.enum(['draft', 'published', 'frozen']);
export type SpreadStatus = z.infer<typeof SpreadStatus>;

export const SpreadVisibility = z.enum(['private', 'instance']);
export type SpreadVisibility = z.infer<typeof SpreadVisibility>;

export const ColumnType = z.enum(['text', 'integer', 'number', 'currency', 'percent', 'boolean', 'date', 'datetime']);
export type ColumnType = z.infer<typeof ColumnType>;

export const ColumnRole = z.enum(['dimension', 'measure', 'time', 'key', 'label']);
export type ColumnRole = z.infer<typeof ColumnRole>;

/** sys.spread */
export const Spread = z.object({
  ...rowBase,
  key: Key,
  nameHe: z.string().min(1),
  domainHe: z.string(),
  descriptionHe: z.string(),
  grainHe: z.string(),
  status: SpreadStatus,
  currentVersion: z.number().int().nonnegative(),
  creatorUserId: Id,
  /** data-spreads-private: private = רק היוצר; instance = כל מי שיש לו גישה לנתונים (פריסה של admin). */
  visibility: SpreadVisibility,
});
export type Spread = z.infer<typeof Spread>;

/** data-spread-publish-explain: מדידת EXPLAIN ANALYZE בפרסום (sys.spread_version.perf). */
export const SpreadPerf = z.object({
  ms: z.number(),
  rows: z.number().nullable(),
  level: z.enum(['ok', 'warn']),
  warnings: z.array(z.object({ kind: z.enum(['nested_lookup', 'seq_scan_large', 'external_sort']), table: z.string().nullable(), column: z.string().nullable(), messageHe: z.string() })),
  measuredAt: z.string(),
  timedOut: z.boolean().optional(),
});
export type SpreadPerf = z.infer<typeof SpreadPerf>;

/** sys.spread_version */
export const SpreadVersion = z.object({
  ...rowBase,
  spreadId: Id,
  version: z.number().int().positive(),
  sql: z.string(),
  sources: z.array(z.object({ worldId: Id, entity: z.string() })),
  definitionsUsed: z.array(Id),
  createdBy: Id,
  noteHe: z.string().nullable(),
  perf: SpreadPerf.nullable(),
});
export type SpreadVersion = z.infer<typeof SpreadVersion>;

/** הגדרת עמודה בלי מזהי שורה — מה ש-SpreadMeta נושא. */
export const SpreadColumnDef = z.object({
  position: z.number().int().nonnegative(),
  nameHe: z.string().min(1),
  descriptionHe: z.string().min(1),
  type: ColumnType,
  role: ColumnRole,
  categorical: z.boolean(),
  entityRef: z.string().nullable(),
  unit: z.string().nullable(),
});
export type SpreadColumnDef = z.infer<typeof SpreadColumnDef>;

/** שם עמודה שהוא יחידת זמן ("חודש", "תאריך חשבונית") — ממד זמן, לא ממד ערכים. */
const TIME_NAME_RE = /^(תאריך|חודש|שנה|רבעון|שבוע|יום)(\s|$)/;
/**
 * עמודה עם אינדקס ערכים (06 §4): `categorical` מפורש, **או** עמודת טקסט בתפקיד ממד (תמיד — אין opt-out). ממד הוא תמיד מה שמסננים לפי
 * ערך ("דירוג 5", "סוג לקוח 13", "קבוצת פוקוס 990") — גם כשהיוצר (אייבי) לא סימן `categorical`; בלי אינדקס, ערך
 * שהמשתמש נקב בו נשמט בשקט והתשובה היא הסך הכולל (גלאור, 2026-10-11). עמודות זמן ("חודש") אינן ממד ערכים.
 */
export const isValueColumn = (c: Pick<SpreadColumnDef, 'nameHe' | 'type' | 'role' | 'categorical'>): boolean =>
  c.categorical || (c.type === 'text' && c.role === 'dimension' && !TIME_NAME_RE.test(c.nameHe));

/** sys.spread_column */
export const SpreadColumn = z.object({ ...rowBase, spreadVersionId: Id, ...SpreadColumnDef.shape });
export type SpreadColumn = z.infer<typeof SpreadColumn>;

/** מה שבינה-3 והקומפיילר רואים על פריסה. */
export const SpreadMeta = z.object({
  key: Key,
  nameHe: z.string(),
  domainHe: z.string(),
  descriptionHe: z.string(),
  grainHe: z.string(),
  columns: z.array(SpreadColumnDef),
  asOf: Ts.nullable(),
  degraded: z.boolean(),
  status: SpreadStatus,
  /** מדידת הפרסום של הגרסה הנוכחית; null = פורסמה לפני המדידה. */
  perf: SpreadPerf.nullable().optional(),
});
export type SpreadMeta = z.infer<typeof SpreadMeta>;

// ---------- הטופס ----------

export type TimePreset =
  | 'all' | 'year_start' | 'month_start' | 'quarter_start' | 'week_start'
  | 'last_month' | 'last_quarter' | 'last_year' | 'last_12m'
  | `last_${number}m` | `last_${number}d`
  | `${number}` | `${number}-${number}` | `${number}-Q${number}`;

export const TIME_PRESET_RE =
  /^(all|year_start|month_start|quarter_start|week_start|last_month|last_quarter|last_year|last_12m|last_[1-9]\d{0,3}[md]|\d{4}|\d{4}-(0[1-9]|1[0-2])|\d{4}-Q[1-4])$/;

export const TimePreset = z.string().regex(TIME_PRESET_RE, 'unknown time preset') as unknown as z.ZodType<TimePreset>;

export const FilterOp = z.enum(['=', '!=', 'in', 'not_in', 'contains', '>', '>=', '<', '<=', 'between', 'is_null', 'not_null']);
export type FilterOp = z.infer<typeof FilterOp>;

export const QueryFilter = z.object({
  column: z.string(),
  op: FilterOp,
  value: z.unknown().optional(),
  values: z.array(z.unknown()).optional(),
  negate: z.boolean().optional(),
});
export type QueryFilter = z.infer<typeof QueryFilter>;

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const QueryTime = z.object({
  column: z.string().optional(),
  preset: TimePreset.optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
});
export type QueryTime = z.infer<typeof QueryTime>;

export const Grain = z.enum(['day', 'week', 'month', 'quarter', 'year']);
export type Grain = z.infer<typeof Grain>;

export const Agg = z.enum(['sum', 'count', 'count_distinct', 'avg', 'min', 'max', 'median']);
export type Agg = z.infer<typeof Agg>;

const FilterSet = z.object({ filters: z.array(QueryFilter).optional(), time: QueryTime.optional() });

/** SpreadQuery — שמות העמודות הם nameHe של עמודות בפריסה. */
export const SpreadQuery = z.object({
  spread: z.string(),
  columns: z.array(z.string()).optional(),
  filters: z.array(QueryFilter).optional(),
  time: QueryTime.optional(),
  groupBy: z.array(z.union([z.string(), z.object({ column: z.string(), grain: Grain })])).optional(),
  measures: z.array(z.object({ column: z.string(), agg: Agg, label: z.string().optional() })).optional(),
  computed: z.array(z.object({ op: z.enum(['ratio', 'pct', 'diff', 'sum']), a: z.string(), b: z.string(), label: z.string() })).optional(),
  compare: z.enum(['prev_year', 'prev_month', 'prev_period']).optional(),
  having: z.array(z.object({ measure: z.string(), op: z.enum(['>', '>=', '<', '<=', '=', '<>']), value: z.number() })).optional(),
  window: z.object({ op: z.enum(['rank', 'running_total', 'share', 'lag']), measure: z.string(), partition: z.array(z.string()).optional() }).optional(),
  top: z.object({ n: z.number().int().positive(), per: z.string().optional(), by: z.string(), dir: z.enum(['desc', 'asc']) }).optional(),
  exclude: FilterSet.optional(),
  require: FilterSet.optional(),
  sort: z.array(z.object({ column: z.string(), dir: z.enum(['asc', 'desc']) })).optional(),
  limit: z.number().int().positive().max(5000).optional(),
  /** דילוג על N שורות ראשונות (דפדוף: offset + limit; בשיחה המודל רואה עד 100 שורות לקריאה). */
  offset: z.number().int().nonnegative().optional(),
  refuse: z.string().optional(),
});
export type SpreadQuery = z.infer<typeof SpreadQuery>;

/** עמודת פלט: ה-SQL משתמש ב-aliases c1..cN, ממופים ל-nameHe. */
export const OutputColumn = z.object({
  id: z.string().regex(/^c[1-9]\d*$/) as unknown as z.ZodType<`c${number}`>,
  nameHe: z.string(),
  type: ColumnType,
  role: ColumnRole,
  source: z.object({ column: z.string(), agg: Agg.optional() }).optional(),
});
export type OutputColumn = z.infer<typeof OutputColumn>;

export const QueryResult = z.object({
  columns: z.array(OutputColumn),
  rows: z.array(Row),
  asOf: Ts.nullable(),
  degraded: z.boolean(),
  truncated: z.boolean(),
  /** ה-offset שהטופס ביקש (רק כשהוא > 0) — לדפדוף: הקריאה הבאה עם offset + rows.length. */
  offset: z.number().int().nonnegative().optional(),
  /** רק ל-creator/admin. */
  sql: z.string().optional(),
});
export type QueryResult = z.infer<typeof QueryResult>;
